import {
  EditorState,
  EditorView,
  markdownLoader,
  type NodeViewConstructor,
  type PMNode,
  resolve,
  Schema,
  setupBase,
  setupBlockquote,
  setupBold,
  setupCallout,
  setupCode,
  setupCodeBlock,
  setupFrontmatter,
  setupHardBreak,
  setupHeading,
  setupHorizontalRule,
  setupImage,
  setupItalic,
  setupLink,
  setupList,
  setupMath,
  setupParagraph,
  setupStrike,
  setupTable,
  setupWikiLink,
} from '@bangle.io/prosemirror-plugins';

import { getCalloutDefaultTitles } from './callout-titles';

const DIRECT_IMAGE_SRC_RE = /^(?:https?:\/\/|blob:|data:image\/)/i;

export type OpenImageLightbox = (src: string, alt: string) => void;

export type ReadonlyMarkdownViewOptions = {
  /** Mount target; ProseMirror replaces its contents. */
  domNode: HTMLElement;
  markdown: string;
  /** Rewrite relative image paths (e.g. share-media URLs). */
  resolveImageSrc?: (src: string) => string;
  /** Open a larger preview when an image is clicked. */
  onOpenImage?: OpenImageLightbox;
};

/**
 * Markdown-relevant subset of the production ProseMirror schema, without
 * editor chrome (slash menu, selection menus, history, drag handles, etc.).
 * Keep registration order aligned with `createProductionMarkdown` / editor
 * extensions so parse/display fidelity matches the app.
 */
function createReadonlyExtensions() {
  return [
    setupBase({ docContent: 'frontmatter? block+' }),
    setupFrontmatter(),
    setupBlockquote(),
    setupCallout({ defaultTitles: getCalloutDefaultTitles() }),
    setupBold(),
    setupList(),
    setupMath(),
    setupHardBreak(),
    setupHeading(),
    setupParagraph(),
    setupStrike(),
    setupWikiLink(),
    setupHorizontalRule(),
    setupCode(),
    setupCodeBlock(),
    setupItalic(),
    setupLink({ openOnClick: true }),
    setupImage(),
    setupTable(),
  ];
}

function bindImageLightboxClick(
  dom: HTMLImageElement,
  onOpen?: OpenImageLightbox,
) {
  if (!onOpen) return;
  dom.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    if (!dom.src || dom.classList.contains('bangle-local-image-broken')) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    onOpen(dom.src, dom.alt || '');
  });
}

function createResolvedImageNodeView({
  resolveImageSrc,
  onOpenImage,
}: {
  resolveImageSrc?: (src: string) => string;
  onOpenImage?: OpenImageLightbox;
}): NodeViewConstructor {
  return (node: PMNode) => {
    const dom = document.createElement('img');
    const alt = typeof node.attrs.alt === 'string' ? node.attrs.alt : '';
    const title = typeof node.attrs.title === 'string' ? node.attrs.title : '';
    const src = typeof node.attrs.src === 'string' ? node.attrs.src : '';
    dom.alt = alt;
    if (title) {
      dom.title = title;
    }
    dom.loading = 'lazy';
    // Failed loads (404 share-media, etc.) must take the broken class so
    // `width: min-content` does not crush alt text into a vertical stack.
    const markBroken = () => {
      dom.classList.add('bangle-local-image-broken');
    };
    dom.addEventListener('error', markBroken);
    if (!src) {
      markBroken();
    } else if (DIRECT_IMAGE_SRC_RE.test(src)) {
      dom.src = src;
    } else if (resolveImageSrc) {
      dom.src = resolveImageSrc(src);
    } else {
      dom.src = src;
    }
    bindImageLightboxClick(dom, onOpenImage);
    return {
      dom,
      destroy: () => {
        dom.removeEventListener('error', markBroken);
      },
    };
  };
}

/**
 * Mounts a non-editable ProseMirror view that renders Markdown with the same
 * schema/plugins used for note display in the editor (headings, lists, math,
 * tables, images, etc.).
 */
export function createReadonlyMarkdownView({
  domNode,
  markdown,
  resolveImageSrc,
  onOpenImage,
}: ReadonlyMarkdownViewOptions): EditorView {
  const extensions = createReadonlyExtensions();
  const resolved = resolve(extensions);
  const schema = new Schema({
    topNode: 'doc',
    nodes: resolved.nodes,
    marks: resolved.marks,
  });
  const { parser } = markdownLoader(extensions, schema);
  const nodeViews =
    resolveImageSrc || onOpenImage
      ? {
          image: createResolvedImageNodeView({
            resolveImageSrc,
            onOpenImage,
          }),
        }
      : undefined;

  return new EditorView(
    { mount: domNode },
    {
      state: EditorState.create({
        doc: parser.parse(markdown || ''),
        schema,
        plugins: resolved.resolvePlugins({ schema }),
      }),
      editable: () => false,
      nodeViews,
      attributes: {
        class: 'ProseMirror-readonly',
        spellcheck: 'false',
      },
    },
  );
}
